import { motion } from "framer-motion";
import { ArrowRight, KeyRound, Mail, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AuthShell from "./AuthShell";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, login, userPrimaryRoute } = useAuth();
  const [method, setMethod] = useState("email");
  const [credentials, setCredentials] = useState({
    identifier: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const from = location.state?.from;
      const redirectTo =
        typeof from === "string"
          ? from
          : from?.pathname
            ? `${from.pathname}${from.search || ""}${from.hash || ""}`
            : userPrimaryRoute;

      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, isLoading, location.state, navigate, userPrimaryRoute]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCredentials((current) => ({ ...current, [name]: value }));
  };

  const validate = () => {
    const nextErrors = {};

    if (method === "email") {
      if (!credentials.identifier.trim()) {
        nextErrors.identifier = "Enter your email or mobile.";
      }
      if (!credentials.password.trim()) {
        nextErrors.password = "Password is required.";
      }
    } else if (!/^\d{10}$/.test(credentials.identifier)) {
      nextErrors.identifier = "Enter a valid 10-digit mobile number.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    if (method === "otp") {
      navigate(`/verify-phone?flow=register&phone=${encodeURIComponent(credentials.identifier)}`);
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/auth/login", credentials);

      if (response.data.success) {
        const nextUser = login(response.data);
        toast.success("Login successful");
        const from = location.state?.from;
        const redirectTo =
          typeof from === "string"
            ? from
            : from?.pathname
              ? `${from.pathname}${from.search || ""}${from.hash || ""}`
              : nextUser.role === "admin"
                ? "/admin-dashboard"
                : nextUser.role === "candidate"
                  ? "/candidate-dashboard"
                  : "/dashboard";

        navigate(redirectTo, { replace: true });
      }
    } catch (error) {
      console.error("Login Error:", error);
      toast.error(error.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back to JanShakti"
      subtitle="Modern civic participation starts with a frictionless sign-in. Use email login now or continue into the phone-based onboarding flow."
      step={1}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            {
              key: "email",
              title: "Email Login",
              description: "Fast login for returning users.",
              icon: Mail,
            },
            {
              key: "otp",
              title: "Phone OTP",
              description: "Continue into the verified phone flow.",
              icon: Smartphone,
            },
          ].map((option) => (
            <motion.button
              key={option.key}
              type="button"
              whileHover={{ y: -2, scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => {
                setMethod(option.key);
                setErrors({});
              }}
              className={`rounded-3xl border p-5 text-left transition ${
                method === option.key
                  ? "border-orange-400 bg-orange-50 shadow-lg shadow-orange-100/80"
                  : "border-slate-200 bg-white hover:border-orange-200"
              }`}
            >
              <option.icon className="h-5 w-5 text-orange-500" />
              <h3 className="mt-4 text-lg font-bold text-slate-900">{option.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{option.description}</p>
            </motion.button>
          ))}
        </div>

        <div className="space-y-4 rounded-3xl border border-slate-200 bg-slate-50/70 p-5">
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              {method === "email" ? "Email or Mobile" : "Mobile Number"}
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-slate-400">
                {method === "email" ? <Mail className="h-4 w-4" /> : <Smartphone className="h-4 w-4" />}
              </span>
              <input
                name="identifier"
                value={credentials.identifier}
                onChange={handleChange}
                placeholder={method === "email" ? "Enter email or mobile" : "Enter 10-digit mobile"}
                className={`w-full rounded-2xl border bg-white py-4 pl-12 pr-4 text-sm outline-none transition ${
                  errors.identifier ? "border-rose-300" : "border-slate-200 focus:border-orange-400"
                }`}
                maxLength={method === "otp" ? 10 : undefined}
              />
            </div>
            {errors.identifier && <p className="mt-2 text-sm text-rose-500">{errors.identifier}</p>}
          </div>

          {method === "email" && (
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Password</label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-slate-400">
                  <KeyRound className="h-4 w-4" />
                </span>
                <input
                  type="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  className={`w-full rounded-2xl border bg-white py-4 pl-12 pr-4 text-sm outline-none transition ${
                    errors.password ? "border-rose-300" : "border-slate-200 focus:border-orange-400"
                  }`}
                />
              </div>
              {errors.password && <p className="mt-2 text-sm text-rose-500">{errors.password}</p>}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-green-500 px-5 py-4 text-sm font-semibold text-white shadow-xl shadow-orange-200/60 transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {loading ? (
            <span className="inline-flex h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : (
            <ArrowRight className="h-4 w-4" />
          )}
          {method === "email" ? "Login" : "Continue with OTP"}
        </button>

        <div className="space-y-2 text-center text-sm text-slate-600">
          <Link to="/forgot-password" className="font-medium text-slate-500 hover:text-orange-600">
            Forgot password?
          </Link>
          <p>
            New user?{" "}
            <Link to="/register" className="font-semibold text-orange-600 hover:text-orange-700">
              Register
            </Link>
          </p>
        </div>
      </form>
    </AuthShell>
  );
};

export default Login;
